import React, { forwardRef } from 'react';
import { Building2, Globe } from 'lucide-react';

const statusLabels = {
  in_review: { en: 'In Review', ar: 'قيد المراجعة', color: '#B45309' },
  documents_pending: { en: 'Documents Pending', ar: 'مستندات معلقة', color: '#C2410C' },
  approved: { en: 'Approved', ar: 'موافق عليه', color: '#1d4ed8' },
  rejected: { en: 'Rejected', ar: 'مرفوض', color: '#BE123C' }
};

const officeLabels = {
  flexi: { en: 'Flexi Desk', ar: 'مكتب مرن' },
  private: { en: 'Private Office', ar: 'مكتب خاص' },
  executive: { en: 'Executive Suite', ar: 'جناح تنفيذي' }
};

const Row = ({ label, value }) => (
  <tr className="border-b border-gray-200 last:border-0">
    <td className="py-2.5 text-xs uppercase tracking-widest text-[#8b6f2f] font-semibold w-1/3 align-top">{label}</td>
    <td className="py-2.5 text-sm text-[#0a0f1c] align-top">{value ?? '—'}</td>
  </tr>
);

const ApplicationView = forwardRef(({ application, lang = 'en' }, ref) => {
  if (!application) return null;
  const a = application;
  const created = a.created_at ? new Date(a.created_at) : null;
  const dateStr = created ? created.toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-GB', { year: 'numeric', month: 'long', day: '2-digit' }) : '—';
  const timeStr = created ? created.toLocaleTimeString(lang === 'ar' ? 'ar-AE' : 'en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const st = statusLabels[a.status] || statusLabels.in_review;
  const TypeIcon = a.type === 'mainland' ? Building2 : Globe;
  const typeLabel = a.type === 'mainland' ? (lang === 'ar' ? 'براً (Mainland)' : 'Mainland') : (lang === 'ar' ? 'منطقة حرة (Freezone)' : 'Freezone');
  const officeLbl = officeLabels[a.office_type]?.[lang] || a.office_type || '—';

  return (
    <div ref={ref} dir={lang === 'ar' ? 'rtl' : 'ltr'} className="application-printable bg-white text-[#0a0f1c] p-10 max-w-3xl mx-auto shadow-2xl print:shadow-none" style={{ fontFamily: lang === 'ar' ? "'Cairo', 'Tajawal', sans-serif" : "'Inter', sans-serif" }}>
      {/* Header */}
      <div className="flex items-start justify-between pb-6 border-b-2 border-[#c9a45c]">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e6c78a 0%, #a8823c 100%)' }}>
              <span className="text-white font-bold text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>i</span>
            </div>
            <div>
              <div className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#0a0f1c' }}>iAccounting</div>
              <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f]">Company Consultation</div>
            </div>
          </div>
          <div className="mt-3 text-[11px] text-gray-600 leading-relaxed">
            Business Bay, Dubai, UAE<br/>
            info@iaccounting.ae &middot; +971 55 109 6418
          </div>
        </div>
        <div className="text-end" dir="ltr">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f]">{lang === 'ar' ? 'طلب تأسيس شركة' : 'Company Setup Application'}</div>
          <div className="text-lg font-mono font-bold text-[#a8823c] mt-1">{a.id}</div>
          <div className="text-xs text-gray-600 mt-2">{dateStr}</div>
          {timeStr && <div className="text-[10px] text-gray-500">{timeStr} GST</div>}
          <span className="inline-block mt-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-semibold" style={{ background: `${st.color}20`, color: st.color, border: `1px solid ${st.color}40` }}>
            {st[lang]}
          </span>
        </div>
      </div>

      {/* Applicant */}
      <div className="grid grid-cols-2 gap-6 mt-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-2">{lang === 'ar' ? 'مقدم الطلب' : 'Applicant'}</div>
          <div className="font-semibold text-base">{a.full_name || '—'}</div>
          {a.email && <div className="text-sm text-gray-600">{a.email}</div>}
          {a.phone && <div className="text-sm text-gray-600">{a.phone}</div>}
        </div>
        <div className="text-end">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-2">{lang === 'ar' ? 'نوع التأسيس' : 'Setup Type'}</div>
          <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#c9a45c] text-[#a8823c]">
            <TypeIcon className="w-4 h-4" />
            <span className="font-medium text-sm">{typeLabel}</span>
          </div>
          {(a.zone_name || a.zone_code) && (
            <div className="mt-2 text-xs text-gray-600">
              {lang === 'ar' ? 'المنطقة' : 'Zone'}: <span className="font-medium text-[#a8823c]">{a.zone_name || a.zone_code}</span>
            </div>
          )}
        </div>
      </div>

      {/* Company details */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-[#8b6f2f] font-semibold border-b border-[#c9a45c] pb-2 mb-3">
          {lang === 'ar' ? 'تفاصيل الشركة' : 'Company Details'}
        </div>
        <table className="w-full border-collapse">
          <tbody>
            <Row label={lang === 'ar' ? 'اسم الشركة' : 'Company Name'} value={a.company_name} />
            <Row label={lang === 'ar' ? 'النشاط' : 'Business Activity'} value={a.activity} />
            <Row label={lang === 'ar' ? 'رأس المال' : 'Share Capital'} value={a.capital ? `${a.capital} AED` : '—'} />
            <Row label={lang === 'ar' ? 'عدد الشركاء' : 'Shareholders'} value={a.shareholders} />
            <Row label={lang === 'ar' ? 'عدد التأشيرات' : 'Visa Quota'} value={a.visas} />
            <Row label={lang === 'ar' ? 'نوع المكتب' : 'Office Type'} value={officeLbl} />
          </tbody>
        </table>
      </div>

      {/* Progress */}
      <div className="mt-8">
        <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[#8b6f2f] font-semibold mb-2">
          <span>{lang === 'ar' ? 'حالة المعالجة' : 'Processing Status'}</span>
          <span className="text-[#a8823c]">{a.progress || 15}%</span>
        </div>
        <div className="h-2 w-full bg-[#f5eede] rounded-full overflow-hidden">
          <div className="h-full" style={{ width: `${a.progress || 15}%`, background: 'linear-gradient(90deg, #c9a45c, #a8823c)' }} />
        </div>
      </div>

      {/* Notes */}
      {a.notes && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#8b6f2f] mb-1">{lang === 'ar' ? 'ملاحظات العميل' : 'Customer Notes'}</div>
          <div className="text-xs text-gray-700 whitespace-pre-wrap bg-[#faf6ec] border-l-4 border-[#c9a45c] p-3 rounded">{a.notes}</div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-10 pt-6 border-t border-gray-200 flex justify-between items-end text-xs text-gray-500">
        <div>
          <div>{lang === 'ar' ? 'تم إنشاؤه بواسطة' : 'Generated by'}: <span className="text-gray-700">iAccounting Portal</span></div>
          <div className="mt-1">{lang === 'ar' ? 'هذا المستند إثبات باستلام طلب التأسيس.' : 'This document acknowledges receipt of the setup application.'}</div>
        </div>
        <div className="text-end">
          <div className="w-40 border-t border-gray-400 mt-8 pt-1">{lang === 'ar' ? 'توقيع وختم' : 'Signature & Stamp'}</div>
        </div>
      </div>
    </div>
  );
});

ApplicationView.displayName = 'ApplicationView';
export default ApplicationView;
